/**
 * Guest-facing localisation.
 *
 * Six dictionaries, no framework. Which one a guest sees is decided, in order:
 *
 *   1. `?lang=xx` in the address — a link the host shares, or the guest's own
 *      choice from the language picker, which writes it there;
 *   2. the guest's earlier pick on this device (localStorage);
 *   3. the event's language, chosen by the host (`events.guest_language`);
 *   4. the browser's preference, when it is one of the six;
 *   5. English.
 *
 * Every dictionary spells out every key. There is deliberately no `...en`
 * spread: a missing translation is a type error and a `verify:i18n` failure,
 * not a sentence of English in the middle of a Spanish page.
 *
 * The key set is the *guest's* surface only: the upload page, the gallery and
 * its lightbox, the guestbook and the wall. The host dashboard stays English.
 *
 * Templates use `{name}` placeholders, filled by `format()`. Counts use paired
 * `…One` / `…Other` keys chosen by `Intl.PluralRules`, which is right for all
 * six languages (none of them needs a "few" or "many" form for these strings).
 *
 * The translations are first drafts and have not had a native-speaker review;
 * docs/i18n.md tracks that per language.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

export type GuestLanguage = "" | "en" | "es" | "fr" | "de" | "it" | "pt";
export type Locale = Exclude<GuestLanguage, "">;

export const LOCALES: readonly Locale[] = ["en", "es", "fr", "de", "it", "pt"];

/** Endonyms: a guest looking for their language looks for its own name. */
export const LOCALE_NAMES: Readonly<Record<Locale, string>> = {
  en: "English",
  es: "Español",
  fr: "Français",
  de: "Deutsch",
  it: "Italiano",
  pt: "Português",
};

export const GUEST_LANGUAGE_LABELS: ReadonlyArray<{
  readonly value: GuestLanguage;
  readonly label: string;
}> = [{ value: "", label: "Automatic (browser)" }, ...LOCALES.map((value) => ({ value, label: LOCALE_NAMES[value] }))];

const en = {
  /* ---- shared ---- */
  loading: "Loading…",
  oneMoment: "One moment.",
  error: "Something went wrong. Try again.",
  footer: "Powered by Eventgram · Your photos stay private to this event",
  languageLabel: "Language",
  privateAlbum: "Private album",
  optional: "optional",
  remove: "Remove",
  clear: "Clear",
  cancel: "Cancel",
  close: "Close",
  all: "All",
  photo: "Photo",
  showMore: "Show more",

  /* ---- upload page ---- */
  uploadTitle: "Share your photos",
  uploadHint: "They appear at the party straight away",
  chooseFiles: "Choose photos",
  camera: "Camera",
  sendQueue: "Send",
  uploading: "Sending…",
  addedToGallery: "Sent! Thank you.",
  addedToGalleryDetail: "They are with the host now. You can add more any time.",
  captionPlaceholder: "Add a caption (optional)",
  captionFor: "Caption for {name}",
  uploadingFile: "Uploading {name}",
  namePlaceholder: "Your name (optional)",
  nameExample: "Priya",
  filterLabel: "Style",
  albumLabel: "Album",
  mainAlbum: "Main gallery",
  openingAlbum: "Opening the album…",
  linkNotFound: "Link not found",
  linkDoesNotExist: "That event link does not exist.",
  firstPhoto: "Be the first to add a photo",
  photosInAlbumOne: "{n} photo in the album",
  photosInAlbumOther: "{n} photos in the album",
  uploadLimitHint: "Photos and videos up to 25 MB. Nothing is sent until you tap Send.",
  sentProgress: "{done} of {total} sent",
  statusReady: "Ready",
  statusSending: "Sending",
  statusSent: "Sent",
  statusFailed: "Failed",
  retryFile: "Retry this file",
  attempts: "{n} attempts",
  sentFromPhone: "Sent from this phone",
  waitingApproval: "Waiting for the host to approve",
  notOnWall: "Not shown on the wall",
  onWall: "On the wall",
  uploadNetworkError: "The connection dropped during the upload. Try again.",
  uploadStalled: "The upload stopped responding. Try again.",
  uploadCancelled: "Upload cancelled.",
  uploadFailed: "The upload did not go through. Try again.",
  fileTypeRefused: "Choose a photo or video (JPEG, PNG, WebP, GIF, HEIC, MP4, MOV or WebM).",
  fileTooLarge: "Larger than 25 MB",

  /* ---- gallery ---- */
  viewGallery: "View the gallery",
  galleryTitle: "Event gallery",
  hostView: "Host view",
  photoCountOne: "{n} photo",
  photoCountOther: "{n} photos",
  taggedWith: "tagged “{tag}”",
  eventFinished: "this event has finished",
  downloadAll: "Download all",
  downloadAllPhotos: "Download these photos",
  preparingDownload: "Preparing your download…",
  selectPhotos: "Select photos",
  selectedOne: "{n} photo selected",
  selectedOther: "{n} photos selected",
  selectAllShown: "Select all shown",
  downloadSelected: "Download selected ({n})",
  downloadStarting: "Your download is starting. A large album can take a moment.",
  downloadFailed: "Could not start the download. Try again.",
  downloadTooLarge: "That is more than one download can hold. Select the photos you want instead.",
  downloadWaitOne: "You have downloaded a lot just now. Try again in {n} minute.",
  downloadWaitOther: "You have downloaded a lot just now. Try again in {n} minutes.",
  downloadMissing: "Some of those photos are no longer available, so they were removed from your selection.",
  filterByAlbum: "Filter photos by album",
  filterByTag: "Filter photos by tag",
  chooseToDownload: "Choose photos to download",
  downloadSelection: "Download selection",
  noTagged: "No photos tagged “{tag}” yet",
  emptyAlbum: "The album is empty",
  tryAnotherTag: "Try another tag, or choose All to see every photo.",
  emptyHint: "Nothing here yet. Photos appear once the host approves them.",
  showMoreLeft: "Show more ({n} left)",
  notFound: "Not found",
  noGallery: "There is no gallery at this address.",
  galleryLoadFailed: "Could not load this gallery.",
  openingGallery: "Opening the gallery…",
  privateGallery: "Private gallery",
  galleryIsPrivate: "This gallery is private",
  enterPassword: "Enter the password the host gave you.",
  passwordLabel: "Password",
  passwordPlaceholder: "Event password",
  openGallery: "Open gallery",
  requiresPassword: "This event needs a password",
  wrongPassword: "That password is wrong.",
  enter: "Enter",

  /* ---- find yourself ---- */
  addYourFace: "Find yourself",
  selfieHint: "Add a selfie and we will look through the album for photos of you. The selfie never leaves this phone: the search runs here, not on a server.",
  takeSelfie: "Add a selfie",
  scanning: "Looking for you…",
  noMatchYet: "Nothing found yet. Try a clearer photo.",
  matches: "Photos with you",

  /* ---- lightbox and tiles ---- */
  photoDialog: "Photo",
  eventPhoto: "Event photo",
  untitled: "Untitled",
  previousPhoto: "Previous photo",
  nextPhoto: "Next photo",
  sharedBy: "Shared by {name}",
  photoTags: "Photo tags",
  autoDescription: "Auto description",
  aiFlagged: "Flagged for review. This photo is blurred in the gallery.",
  like: "Like",
  liked: "Liked",
  doubleTapToLike: "Double-tap the photo to like it",
  comment: "Comment",
  comments: "Comments",
  noComments: "Nothing yet.",
  you: "(you)",
  deleteCommentFrom: "Delete the comment from {name}",
  deleting: "Deleting…",
  delete: "Delete",
  addComment: "Add a comment",
  commentPlaceholder: "Write something…",
  post: "Post",
  hostCanRemove: "You are the host, so you can remove any comment.",
  saveFailed: "Could not save that.",
  postFailed: "Could not post that.",
  deleteFailed: "Could not delete that.",
  selectPhoto: "Select {name}",
  hiddenByHost: "Hidden by the host",
  couldNotLoad: "Could not load",
  pinned: "Pinned",
  pinnedToWall: "Pinned to the wall",

  /* ---- guestbook ---- */
  guestbook: "Guestbook",
  guestbookPlaceholder: "Leave a note for the hosts",
  record: "Record a voice note",
  stop: "Stop",
  send: "Send",
  sending: "Sending…",
  emptyGuestbook: "No notes yet. Be the first!",
  removeMine: "Remove my note",
  waitingForHost: "Waiting for the host",
  heldNotice: "Thank you! Your note will appear once the host has seen it.",
  previewHint: "Listen back before you send it.",
  sendVoice: "Send voice note",
  recordAgain: "Record again",
  discard: "Discard",
  micDenied: "Microphone access was blocked. Allow it in your browser's site settings, then try again.",

  /* ---- wall ---- */
  wallScan: "Scan to add your photos",
  wallFirstPhoto: "Scan to add the first photo.",
  wallWaiting: "Waiting for the first photo.",
  slideShow: "Enjoying the party? Add your photos",

  /* ---- server errors a guest can meet, by code ---- */
  errRateLimited: "That is a lot at once. Try again in a little while.",
  errRateLimitedEvent: "This event is getting a lot of uploads right now. Try again in a little while.",
  errTooLarge: "That file is too large to send.",
  errUnsupported: "That kind of file cannot be added here.",
  errQuota: "The host is out of storage, so this event cannot take photos right now.",
  errClosed: "This event is not taking new additions any more.",
  errNotFound: "That link does not exist, or the event was removed.",
  errForbidden: "That is not allowed for this event.",
  errOffline: "You seem to be offline. Check the connection and try again.",
} as const;

/**
 * Keys must match, values must not. `as const` on `en` otherwise freezes each
 * value's exact English wording into the type, which would forbid a dictionary
 * from saying the same sentence differently — the one thing a translation must
 * be allowed to do.
 */
export type GuestCopyKey = keyof typeof en;
type Dictionary = Readonly<Record<GuestCopyKey, string>>;

const es: Dictionary = {
  loading: "Cargando…",
  oneMoment: "Un momento.",
  error: "Algo salió mal. Inténtalo de nuevo.",
  footer: "Con Eventgram · Tus fotos son privadas de este evento",
  languageLabel: "Idioma",
  privateAlbum: "Álbum privado",
  optional: "opcional",
  remove: "Quitar",
  clear: "Vaciar",
  cancel: "Cancelar",
  close: "Cerrar",
  all: "Todas",
  photo: "Foto",
  showMore: "Ver más",
  uploadTitle: "Comparte tus fotos",
  uploadHint: "Aparecen en la fiesta al instante",
  chooseFiles: "Elegir fotos",
  camera: "Cámara",
  sendQueue: "Enviar",
  uploading: "Enviando…",
  addedToGallery: "¡Enviado! Gracias.",
  addedToGalleryDetail: "Ya las tienen los anfitriones. Puedes añadir más cuando quieras.",
  captionPlaceholder: "Añade un texto (opcional)",
  captionFor: "Texto para {name}",
  uploadingFile: "Enviando {name}",
  namePlaceholder: "Tu nombre (opcional)",
  nameExample: "Lucía",
  filterLabel: "Estilo",
  albumLabel: "Álbum",
  mainAlbum: "Galería principal",
  openingAlbum: "Abriendo el álbum…",
  linkNotFound: "Enlace no encontrado",
  linkDoesNotExist: "Ese enlace del evento no existe.",
  firstPhoto: "Sé la primera persona en añadir una foto",
  photosInAlbumOne: "{n} foto en el álbum",
  photosInAlbumOther: "{n} fotos en el álbum",
  uploadLimitHint: "Fotos y vídeos de hasta 25 MB. No se envía nada hasta que pulses Enviar.",
  sentProgress: "{done} de {total} enviadas",
  statusReady: "Lista",
  statusSending: "Enviando",
  statusSent: "Enviada",
  statusFailed: "Falló",
  retryFile: "Reintentar este archivo",
  attempts: "{n} intentos",
  sentFromPhone: "Enviadas desde este teléfono",
  waitingApproval: "Esperando la aprobación de los anfitriones",
  notOnWall: "No se muestra en la pantalla",
  onWall: "En la pantalla",
  uploadNetworkError: "Se cortó la conexión durante el envío. Inténtalo de nuevo.",
  uploadStalled: "El envío dejó de responder. Inténtalo de nuevo.",
  uploadCancelled: "Envío cancelado.",
  uploadFailed: "El envío no se completó. Inténtalo de nuevo.",
  fileTypeRefused: "Elige una foto o un vídeo (JPEG, PNG, WebP, GIF, HEIC, MP4, MOV o WebM).",
  fileTooLarge: "Supera los 25 MB",
  viewGallery: "Ver la galería",
  galleryTitle: "Galería del evento",
  hostView: "Vista de anfitrión",
  photoCountOne: "{n} foto",
  photoCountOther: "{n} fotos",
  taggedWith: "con la etiqueta «{tag}»",
  eventFinished: "este evento ha terminado",
  downloadAll: "Descargar todo",
  downloadAllPhotos: "Descargar estas fotos",
  preparingDownload: "Preparando tu descarga…",
  selectPhotos: "Seleccionar fotos",
  selectedOne: "{n} foto seleccionada",
  selectedOther: "{n} fotos seleccionadas",
  selectAllShown: "Seleccionar las visibles",
  downloadSelected: "Descargar selección ({n})",
  downloadStarting: "Tu descarga está empezando. Un álbum grande puede tardar un poco.",
  downloadFailed: "No se pudo iniciar la descarga. Inténtalo de nuevo.",
  downloadTooLarge: "Es más de lo que cabe en una descarga. Selecciona las fotos que quieras.",
  downloadWaitOne: "Has descargado mucho en poco tiempo. Vuelve a intentarlo en {n} minuto.",
  downloadWaitOther: "Has descargado mucho en poco tiempo. Vuelve a intentarlo en {n} minutos.",
  downloadMissing: "Algunas de esas fotos ya no están disponibles y se han quitado de tu selección.",
  filterByAlbum: "Filtrar fotos por álbum",
  filterByTag: "Filtrar fotos por etiqueta",
  chooseToDownload: "Elige las fotos que quieres descargar",
  downloadSelection: "Descargar la selección",
  noTagged: "Aún no hay fotos con la etiqueta «{tag}»",
  emptyAlbum: "El álbum está vacío",
  tryAnotherTag: "Prueba otra etiqueta o elige Todas para ver todas las fotos.",
  emptyHint: "Aún no hay nada. Las fotos aparecen cuando los anfitriones las aprueban.",
  showMoreLeft: "Ver más (quedan {n})",
  notFound: "No encontrado",
  noGallery: "No hay ninguna galería en esta dirección.",
  galleryLoadFailed: "No se pudo cargar esta galería.",
  openingGallery: "Abriendo la galería…",
  privateGallery: "Galería privada",
  galleryIsPrivate: "Esta galería es privada",
  enterPassword: "Introduce la contraseña que te dieron los anfitriones.",
  passwordLabel: "Contraseña",
  passwordPlaceholder: "Contraseña del evento",
  openGallery: "Abrir la galería",
  requiresPassword: "Este evento necesita una contraseña",
  wrongPassword: "La contraseña no es correcta.",
  enter: "Entrar",
  addYourFace: "Encuéntrate",
  selfieHint: "Añade un selfi y buscaremos en el álbum las fotos en las que sales. El selfi nunca sale de este teléfono: la búsqueda se hace aquí, no en un servidor.",
  takeSelfie: "Añadir un selfi",
  scanning: "Buscándote…",
  noMatchYet: "Aún no hay resultados. Prueba con una foto más nítida.",
  matches: "Fotos en las que sales",
  photoDialog: "Foto",
  eventPhoto: "Foto del evento",
  untitled: "Sin título",
  previousPhoto: "Foto anterior",
  nextPhoto: "Foto siguiente",
  sharedBy: "Compartida por {name}",
  photoTags: "Etiquetas de la foto",
  autoDescription: "Descripción automática",
  aiFlagged: "Marcada para revisión. Esta foto aparece difuminada en la galería.",
  like: "Me gusta",
  liked: "Te gusta",
  doubleTapToLike: "Toca dos veces la foto para darle me gusta",
  comment: "Comentar",
  comments: "Comentarios",
  noComments: "Aún no hay nada.",
  you: "(tú)",
  deleteCommentFrom: "Borrar el comentario de {name}",
  deleting: "Borrando…",
  delete: "Borrar",
  addComment: "Añade un comentario",
  commentPlaceholder: "Escribe algo…",
  post: "Publicar",
  hostCanRemove: "Eres el anfitrión, así que puedes quitar cualquier comentario.",
  saveFailed: "No se pudo guardar.",
  postFailed: "No se pudo publicar.",
  deleteFailed: "No se pudo borrar.",
  selectPhoto: "Seleccionar {name}",
  hiddenByHost: "Ocultada por los anfitriones",
  couldNotLoad: "No se pudo cargar",
  pinned: "Fijada",
  pinnedToWall: "Fijada en la pantalla",
  guestbook: "Libro de visitas",
  guestbookPlaceholder: "Deja un mensaje a los anfitriones",
  record: "Grabar un mensaje de voz",
  stop: "Parar",
  send: "Enviar",
  sending: "Enviando…",
  emptyGuestbook: "Aún no hay mensajes. ¡Sé la primera persona!",
  removeMine: "Quitar mi mensaje",
  waitingForHost: "Esperando a los anfitriones",
  heldNotice: "¡Gracias! Tu mensaje aparecerá cuando lo hayan visto los anfitriones.",
  previewHint: "Escúchalo antes de enviarlo.",
  sendVoice: "Enviar mensaje de voz",
  recordAgain: "Grabar de nuevo",
  discard: "Descartar",
  micDenied: "Se bloqueó el acceso al micrófono. Permítelo en los ajustes del sitio de tu navegador y vuelve a intentarlo.",
  wallScan: "Escanea para añadir tus fotos",
  wallFirstPhoto: "Escanea para añadir la primera foto.",
  wallWaiting: "Esperando la primera foto.",
  slideShow: "¿Lo estás pasando bien? Añade tus fotos",
  errRateLimited: "Es mucho de golpe. Vuelve a intentarlo dentro de un rato.",
  errRateLimitedEvent: "Este evento está recibiendo muchas fotos ahora mismo. Vuelve a intentarlo dentro de un rato.",
  errTooLarge: "Ese archivo es demasiado grande para enviarlo.",
  errUnsupported: "Ese tipo de archivo no se puede añadir aquí.",
  errQuota: "Los anfitriones se han quedado sin espacio, así que este evento no puede recibir fotos ahora.",
  errClosed: "Este evento ya no acepta nuevas aportaciones.",
  errNotFound: "Ese enlace no existe o el evento se ha eliminado.",
  errForbidden: "Eso no está permitido en este evento.",
  errOffline: "Parece que no tienes conexión. Compruébala y vuelve a intentarlo.",
};

const fr: Dictionary = {
  loading: "Chargement…",
  oneMoment: "Un instant.",
  error: "Une erreur s'est produite. Réessayez.",
  footer: "Propulsé par Eventgram · Vos photos restent privées à cet événement",
  languageLabel: "Langue",
  privateAlbum: "Album privé",
  optional: "facultatif",
  remove: "Retirer",
  clear: "Vider",
  cancel: "Annuler",
  close: "Fermer",
  all: "Toutes",
  photo: "Photo",
  showMore: "Voir plus",
  uploadTitle: "Partagez vos photos",
  uploadHint: "Elles apparaissent tout de suite à la fête",
  chooseFiles: "Choisir des photos",
  camera: "Appareil photo",
  sendQueue: "Envoyer",
  uploading: "Envoi…",
  addedToGallery: "Envoyé ! Merci.",
  addedToGalleryDetail: "Elles sont chez les hôtes. Vous pouvez en ajouter d'autres à tout moment.",
  captionPlaceholder: "Ajoutez une légende (facultatif)",
  captionFor: "Légende pour {name}",
  uploadingFile: "Envoi de {name}",
  namePlaceholder: "Votre nom (facultatif)",
  nameExample: "Camille",
  filterLabel: "Style",
  albumLabel: "Album",
  mainAlbum: "Galerie principale",
  openingAlbum: "Ouverture de l'album…",
  linkNotFound: "Lien introuvable",
  linkDoesNotExist: "Ce lien d'événement n'existe pas.",
  firstPhoto: "Soyez le premier à ajouter une photo",
  photosInAlbumOne: "{n} photo dans l'album",
  photosInAlbumOther: "{n} photos dans l'album",
  uploadLimitHint: "Photos et vidéos jusqu'à 25 Mo. Rien n'est envoyé avant que vous touchiez Envoyer.",
  sentProgress: "{done} sur {total} envoyées",
  statusReady: "Prête",
  statusSending: "Envoi",
  statusSent: "Envoyée",
  statusFailed: "Échec",
  retryFile: "Réessayer ce fichier",
  attempts: "{n} tentatives",
  sentFromPhone: "Envoyées depuis ce téléphone",
  waitingApproval: "En attente de validation par les hôtes",
  notOnWall: "Non affichée à l'écran",
  onWall: "À l'écran",
  uploadNetworkError: "La connexion a été coupée pendant l'envoi. Réessayez.",
  uploadStalled: "L'envoi ne répond plus. Réessayez.",
  uploadCancelled: "Envoi annulé.",
  uploadFailed: "L'envoi n'a pas abouti. Réessayez.",
  fileTypeRefused: "Choisissez une photo ou une vidéo (JPEG, PNG, WebP, GIF, HEIC, MP4, MOV ou WebM).",
  fileTooLarge: "Plus de 25 Mo",
  viewGallery: "Voir la galerie",
  galleryTitle: "Galerie de l'événement",
  hostView: "Vue hôte",
  photoCountOne: "{n} photo",
  photoCountOther: "{n} photos",
  taggedWith: "avec l'étiquette « {tag} »",
  eventFinished: "cet événement est terminé",
  downloadAll: "Tout télécharger",
  downloadAllPhotos: "Télécharger ces photos",
  preparingDownload: "Préparation du téléchargement…",
  selectPhotos: "Sélectionner des photos",
  selectedOne: "{n} photo sélectionnée",
  selectedOther: "{n} photos sélectionnées",
  selectAllShown: "Tout sélectionner",
  downloadSelected: "Télécharger la sélection ({n})",
  downloadStarting: "Votre téléchargement commence. Un grand album peut prendre un moment.",
  downloadFailed: "Impossible de lancer le téléchargement. Réessayez.",
  downloadTooLarge: "C'est plus qu'un seul téléchargement ne peut contenir. Sélectionnez plutôt les photos voulues.",
  downloadWaitOne: "Vous avez beaucoup téléchargé à l'instant. Réessayez dans {n} minute.",
  downloadWaitOther: "Vous avez beaucoup téléchargé à l'instant. Réessayez dans {n} minutes.",
  downloadMissing: "Certaines de ces photos ne sont plus disponibles ; elles ont été retirées de votre sélection.",
  filterByAlbum: "Filtrer les photos par album",
  filterByTag: "Filtrer les photos par étiquette",
  chooseToDownload: "Choisir les photos à télécharger",
  downloadSelection: "Télécharger la sélection",
  noTagged: "Aucune photo avec l'étiquette « {tag} » pour l'instant",
  emptyAlbum: "L'album est vide",
  tryAnotherTag: "Essayez une autre étiquette ou choisissez Toutes pour tout voir.",
  emptyHint: "Rien pour l'instant. Les photos apparaissent une fois validées par les hôtes.",
  showMoreLeft: "Voir plus ({n} restantes)",
  notFound: "Introuvable",
  noGallery: "Il n'y a pas de galerie à cette adresse.",
  galleryLoadFailed: "Impossible de charger cette galerie.",
  openingGallery: "Ouverture de la galerie…",
  privateGallery: "Galerie privée",
  galleryIsPrivate: "Cette galerie est privée",
  enterPassword: "Saisissez le mot de passe donné par les hôtes.",
  passwordLabel: "Mot de passe",
  passwordPlaceholder: "Mot de passe de l'événement",
  openGallery: "Ouvrir la galerie",
  requiresPassword: "Cet événement demande un mot de passe",
  wrongPassword: "Ce mot de passe est incorrect.",
  enter: "Entrer",
  addYourFace: "Retrouvez-vous",
  selfieHint: "Ajoutez un selfie et nous chercherons dans l'album les photos où vous apparaissez. Le selfie ne quitte jamais ce téléphone : la recherche se fait ici, pas sur un serveur.",
  takeSelfie: "Ajouter un selfie",
  scanning: "On vous cherche…",
  noMatchYet: "Rien trouvé pour l'instant. Essayez une photo plus nette.",
  matches: "Photos où vous apparaissez",
  photoDialog: "Photo",
  eventPhoto: "Photo de l'événement",
  untitled: "Sans titre",
  previousPhoto: "Photo précédente",
  nextPhoto: "Photo suivante",
  sharedBy: "Partagée par {name}",
  photoTags: "Étiquettes de la photo",
  autoDescription: "Description automatique",
  aiFlagged: "Signalée pour vérification. Cette photo est floutée dans la galerie.",
  like: "J'aime",
  liked: "Aimée",
  doubleTapToLike: "Touchez deux fois la photo pour l’aimer",
  comment: "Commenter",
  comments: "Commentaires",
  noComments: "Rien pour l'instant.",
  you: "(vous)",
  deleteCommentFrom: "Supprimer le commentaire de {name}",
  deleting: "Suppression…",
  delete: "Supprimer",
  addComment: "Ajouter un commentaire",
  commentPlaceholder: "Écrivez quelque chose…",
  post: "Publier",
  hostCanRemove: "Vous êtes l'hôte : vous pouvez retirer n'importe quel commentaire.",
  saveFailed: "Impossible d'enregistrer.",
  postFailed: "Impossible de publier.",
  deleteFailed: "Impossible de supprimer.",
  selectPhoto: "Sélectionner {name}",
  hiddenByHost: "Masquée par les hôtes",
  couldNotLoad: "Chargement impossible",
  pinned: "Épinglée",
  pinnedToWall: "Épinglée à l'écran",
  guestbook: "Livre d'or",
  guestbookPlaceholder: "Laissez un mot aux hôtes",
  record: "Enregistrer un message vocal",
  stop: "Arrêter",
  send: "Envoyer",
  sending: "Envoi…",
  emptyGuestbook: "Pas encore de message. Soyez le premier !",
  removeMine: "Retirer mon message",
  waitingForHost: "En attente des hôtes",
  heldNotice: "Merci ! Votre message apparaîtra une fois lu par les hôtes.",
  previewHint: "Réécoutez-le avant de l'envoyer.",
  sendVoice: "Envoyer le message vocal",
  recordAgain: "Recommencer",
  discard: "Supprimer",
  micDenied: "L'accès au micro a été bloqué. Autorisez-le dans les réglages du site de votre navigateur, puis réessayez.",
  wallScan: "Scannez pour ajouter vos photos",
  wallFirstPhoto: "Scannez pour ajouter la première photo.",
  wallWaiting: "En attente de la première photo.",
  slideShow: "Vous profitez de la fête ? Ajoutez vos photos",
  errRateLimited: "C'est beaucoup d'un coup. Réessayez dans un moment.",
  errRateLimitedEvent: "Cet événement reçoit beaucoup de photos en ce moment. Réessayez dans un moment.",
  errTooLarge: "Ce fichier est trop volumineux pour être envoyé.",
  errUnsupported: "Ce type de fichier ne peut pas être ajouté ici.",
  errQuota: "Les hôtes n'ont plus d'espace : cet événement ne peut pas recevoir de photos pour le moment.",
  errClosed: "Cet événement n'accepte plus de nouveaux ajouts.",
  errNotFound: "Ce lien n'existe pas, ou l'événement a été supprimé.",
  errForbidden: "Ce n'est pas autorisé pour cet événement.",
  errOffline: "Vous semblez hors ligne. Vérifiez la connexion et réessayez.",
};

const de: Dictionary = {
  loading: "Wird geladen…",
  oneMoment: "Einen Moment.",
  error: "Etwas ist schiefgelaufen. Bitte noch einmal versuchen.",
  footer: "Mit Eventgram · Deine Fotos bleiben privat für dieses Event",
  languageLabel: "Sprache",
  privateAlbum: "Privates Album",
  optional: "optional",
  remove: "Entfernen",
  clear: "Leeren",
  cancel: "Abbrechen",
  close: "Schließen",
  all: "Alle",
  photo: "Foto",
  showMore: "Mehr anzeigen",
  uploadTitle: "Teile deine Fotos",
  uploadHint: "Sie erscheinen sofort auf der Feier",
  chooseFiles: "Fotos auswählen",
  camera: "Kamera",
  sendQueue: "Senden",
  uploading: "Wird gesendet…",
  addedToGallery: "Gesendet! Danke.",
  addedToGalleryDetail: "Sie sind jetzt bei den Gastgebern. Du kannst jederzeit weitere hinzufügen.",
  captionPlaceholder: "Bildunterschrift hinzufügen (optional)",
  captionFor: "Bildunterschrift für {name}",
  uploadingFile: "{name} wird gesendet",
  namePlaceholder: "Dein Name (optional)",
  nameExample: "Lena",
  filterLabel: "Stil",
  albumLabel: "Album",
  mainAlbum: "Hauptgalerie",
  openingAlbum: "Album wird geöffnet…",
  linkNotFound: "Link nicht gefunden",
  linkDoesNotExist: "Diesen Event-Link gibt es nicht.",
  firstPhoto: "Füge als Erste oder Erster ein Foto hinzu",
  photosInAlbumOne: "{n} Foto im Album",
  photosInAlbumOther: "{n} Fotos im Album",
  uploadLimitHint: "Fotos und Videos bis 25 MB. Gesendet wird erst, wenn du auf Senden tippst.",
  sentProgress: "{done} von {total} gesendet",
  statusReady: "Bereit",
  statusSending: "Wird gesendet",
  statusSent: "Gesendet",
  statusFailed: "Fehlgeschlagen",
  retryFile: "Diese Datei erneut senden",
  attempts: "{n} Versuche",
  sentFromPhone: "Von diesem Handy gesendet",
  waitingApproval: "Wartet auf die Freigabe der Gastgeber",
  notOnWall: "Wird nicht auf der Leinwand gezeigt",
  onWall: "Auf der Leinwand",
  uploadNetworkError: "Die Verbindung ist beim Senden abgebrochen. Bitte noch einmal versuchen.",
  uploadStalled: "Das Senden reagiert nicht mehr. Bitte noch einmal versuchen.",
  uploadCancelled: "Senden abgebrochen.",
  uploadFailed: "Das Senden hat nicht geklappt. Bitte noch einmal versuchen.",
  fileTypeRefused: "Wähle ein Foto oder Video (JPEG, PNG, WebP, GIF, HEIC, MP4, MOV oder WebM).",
  fileTooLarge: "Größer als 25 MB",
  viewGallery: "Galerie ansehen",
  galleryTitle: "Event-Galerie",
  hostView: "Gastgeber-Ansicht",
  photoCountOne: "{n} Foto",
  photoCountOther: "{n} Fotos",
  taggedWith: "mit dem Schlagwort „{tag}“",
  eventFinished: "dieses Event ist vorbei",
  downloadAll: "Alle herunterladen",
  downloadAllPhotos: "Diese Fotos herunterladen",
  preparingDownload: "Download wird vorbereitet…",
  selectPhotos: "Fotos auswählen",
  selectedOne: "{n} Foto ausgewählt",
  selectedOther: "{n} Fotos ausgewählt",
  selectAllShown: "Alle angezeigten auswählen",
  downloadSelected: "Auswahl herunterladen ({n})",
  downloadStarting: "Dein Download startet. Ein großes Album kann einen Moment dauern.",
  downloadFailed: "Der Download konnte nicht gestartet werden. Bitte noch einmal versuchen.",
  downloadTooLarge: "Das ist mehr, als in einen Download passt. Wähle stattdessen die gewünschten Fotos aus.",
  downloadWaitOne: "Du hast gerade sehr viel heruntergeladen. Versuche es in {n} Minute noch einmal.",
  downloadWaitOther: "Du hast gerade sehr viel heruntergeladen. Versuche es in {n} Minuten noch einmal.",
  downloadMissing: "Einige dieser Fotos sind nicht mehr verfügbar und wurden aus deiner Auswahl entfernt.",
  filterByAlbum: "Fotos nach Album filtern",
  filterByTag: "Fotos nach Schlagwort filtern",
  chooseToDownload: "Fotos zum Herunterladen auswählen",
  downloadSelection: "Auswahl herunterladen",
  noTagged: "Noch keine Fotos mit dem Schlagwort „{tag}“",
  emptyAlbum: "Das Album ist leer",
  tryAnotherTag: "Probiere ein anderes Schlagwort oder wähle Alle, um jedes Foto zu sehen.",
  emptyHint: "Noch nichts da. Fotos erscheinen, sobald die Gastgeber sie freigeben.",
  showMoreLeft: "Mehr anzeigen (noch {n})",
  notFound: "Nicht gefunden",
  noGallery: "Unter dieser Adresse gibt es keine Galerie.",
  galleryLoadFailed: "Diese Galerie konnte nicht geladen werden.",
  openingGallery: "Galerie wird geöffnet…",
  privateGallery: "Private Galerie",
  galleryIsPrivate: "Diese Galerie ist privat",
  enterPassword: "Gib das Passwort ein, das du von den Gastgebern bekommen hast.",
  passwordLabel: "Passwort",
  passwordPlaceholder: "Event-Passwort",
  openGallery: "Galerie öffnen",
  requiresPassword: "Dieses Event braucht ein Passwort",
  wrongPassword: "Das Passwort ist falsch.",
  enter: "Öffnen",
  addYourFace: "Finde dich",
  selfieHint: "Füge ein Selfie hinzu, und wir suchen im Album nach Fotos von dir. Das Selfie verlässt dieses Handy nie: Die Suche läuft hier, nicht auf einem Server.",
  takeSelfie: "Selfie hinzufügen",
  scanning: "Wir suchen dich…",
  noMatchYet: "Noch nichts gefunden. Probiere ein schärferes Foto.",
  matches: "Fotos mit dir",
  photoDialog: "Foto",
  eventPhoto: "Event-Foto",
  untitled: "Ohne Titel",
  previousPhoto: "Vorheriges Foto",
  nextPhoto: "Nächstes Foto",
  sharedBy: "Geteilt von {name}",
  photoTags: "Schlagwörter des Fotos",
  autoDescription: "Automatische Beschreibung",
  aiFlagged: "Zur Prüfung markiert. Dieses Foto ist in der Galerie unscharf gestellt.",
  like: "Gefällt mir",
  liked: "Gefällt dir",
  doubleTapToLike: "Tippe zweimal auf das Foto, um es zu liken",
  comment: "Kommentieren",
  comments: "Kommentare",
  noComments: "Noch nichts da.",
  you: "(du)",
  deleteCommentFrom: "Kommentar von {name} löschen",
  deleting: "Wird gelöscht…",
  delete: "Löschen",
  addComment: "Kommentar schreiben",
  commentPlaceholder: "Schreib etwas…",
  post: "Posten",
  hostCanRemove: "Du bist Gastgeber und kannst jeden Kommentar entfernen.",
  saveFailed: "Konnte nicht gespeichert werden.",
  postFailed: "Konnte nicht gepostet werden.",
  deleteFailed: "Konnte nicht gelöscht werden.",
  selectPhoto: "{name} auswählen",
  hiddenByHost: "Von den Gastgebern ausgeblendet",
  couldNotLoad: "Konnte nicht geladen werden",
  pinned: "Angeheftet",
  pinnedToWall: "An die Leinwand angeheftet",
  guestbook: "Gästebuch",
  guestbookPlaceholder: "Hinterlasse den Gastgebern eine Nachricht",
  record: "Sprachnachricht aufnehmen",
  stop: "Stopp",
  send: "Senden",
  sending: "Wird gesendet…",
  emptyGuestbook: "Noch keine Nachrichten. Mach den Anfang!",
  removeMine: "Meine Nachricht entfernen",
  waitingForHost: "Wartet auf die Gastgeber",
  heldNotice: "Danke! Deine Nachricht erscheint, sobald die Gastgeber sie gesehen haben.",
  previewHint: "Hör sie dir an, bevor du sie sendest.",
  sendVoice: "Sprachnachricht senden",
  recordAgain: "Neu aufnehmen",
  discard: "Verwerfen",
  micDenied: "Der Zugriff aufs Mikrofon wurde blockiert. Erlaube ihn in den Website-Einstellungen deines Browsers und versuche es noch einmal.",
  wallScan: "Scannen und Fotos hinzufügen",
  wallFirstPhoto: "Scannen und das erste Foto hinzufügen.",
  wallWaiting: "Warten auf das erste Foto.",
  slideShow: "Gefällt dir die Feier? Füge deine Fotos hinzu",
  errRateLimited: "Das ist viel auf einmal. Versuche es gleich noch einmal.",
  errRateLimitedEvent: "Bei dieser Veranstaltung gehen gerade sehr viele Uploads ein. Versuche es gleich noch einmal.",
  errTooLarge: "Diese Datei ist zu groß zum Senden.",
  errUnsupported: "Diese Art von Datei kann hier nicht hinzugefügt werden.",
  errQuota: "Die Gastgeber haben keinen Speicherplatz mehr, deshalb kann dieses Event gerade keine Fotos annehmen.",
  errClosed: "Dieses Event nimmt keine neuen Beiträge mehr an.",
  errNotFound: "Diesen Link gibt es nicht, oder das Event wurde entfernt.",
  errForbidden: "Das ist bei diesem Event nicht erlaubt.",
  errOffline: "Du scheinst offline zu sein. Prüfe die Verbindung und versuche es noch einmal.",
};

const it: Dictionary = {
  loading: "Caricamento…",
  oneMoment: "Un momento.",
  error: "Qualcosa è andato storto. Riprova.",
  footer: "Con Eventgram · Le tue foto restano private per questo evento",
  languageLabel: "Lingua",
  privateAlbum: "Album privato",
  optional: "facoltativo",
  remove: "Rimuovi",
  clear: "Svuota",
  cancel: "Annulla",
  close: "Chiudi",
  all: "Tutte",
  photo: "Foto",
  showMore: "Mostra altro",
  uploadTitle: "Condividi le tue foto",
  uploadHint: "Compaiono subito alla festa",
  chooseFiles: "Scegli le foto",
  camera: "Fotocamera",
  sendQueue: "Invia",
  uploading: "Invio…",
  addedToGallery: "Inviato! Grazie.",
  addedToGalleryDetail: "Ora le hanno i padroni di casa. Puoi aggiungerne altre quando vuoi.",
  captionPlaceholder: "Aggiungi una didascalia (facoltativa)",
  captionFor: "Didascalia per {name}",
  uploadingFile: "Invio di {name}",
  namePlaceholder: "Il tuo nome (facoltativo)",
  nameExample: "Giulia",
  filterLabel: "Stile",
  albumLabel: "Album",
  mainAlbum: "Galleria principale",
  openingAlbum: "Apertura dell'album…",
  linkNotFound: "Link non trovato",
  linkDoesNotExist: "Questo link dell'evento non esiste.",
  firstPhoto: "Aggiungi tu la prima foto",
  photosInAlbumOne: "{n} foto nell'album",
  photosInAlbumOther: "{n} foto nell'album",
  uploadLimitHint: "Foto e video fino a 25 MB. Non viene inviato nulla finché non tocchi Invia.",
  sentProgress: "{done} di {total} inviate",
  statusReady: "Pronta",
  statusSending: "Invio",
  statusSent: "Inviata",
  statusFailed: "Non riuscita",
  retryFile: "Riprova questo file",
  attempts: "{n} tentativi",
  sentFromPhone: "Inviate da questo telefono",
  waitingApproval: "In attesa dell'approvazione dei padroni di casa",
  notOnWall: "Non mostrata sullo schermo",
  onWall: "Sullo schermo",
  uploadNetworkError: "La connessione si è interrotta durante l'invio. Riprova.",
  uploadStalled: "L'invio ha smesso di rispondere. Riprova.",
  uploadCancelled: "Invio annullato.",
  uploadFailed: "L'invio non è andato a buon fine. Riprova.",
  fileTypeRefused: "Scegli una foto o un video (JPEG, PNG, WebP, GIF, HEIC, MP4, MOV o WebM).",
  fileTooLarge: "Più di 25 MB",
  viewGallery: "Guarda la galleria",
  galleryTitle: "Galleria dell'evento",
  hostView: "Vista organizzatore",
  photoCountOne: "{n} foto",
  photoCountOther: "{n} foto",
  taggedWith: "con l'etichetta «{tag}»",
  eventFinished: "questo evento è terminato",
  downloadAll: "Scarica tutto",
  downloadAllPhotos: "Scarica queste foto",
  preparingDownload: "Preparazione del download…",
  selectPhotos: "Seleziona le foto",
  selectedOne: "{n} foto selezionata",
  selectedOther: "{n} foto selezionate",
  selectAllShown: "Seleziona quelle visibili",
  downloadSelected: "Scarica la selezione ({n})",
  downloadStarting: "Il download sta iniziando. Un album grande può richiedere un momento.",
  downloadFailed: "Impossibile avviare il download. Riprova.",
  downloadTooLarge: "È più di quanto stia in un solo download. Seleziona invece le foto che vuoi.",
  downloadWaitOne: "Hai scaricato molto in poco tempo. Riprova tra {n} minuto.",
  downloadWaitOther: "Hai scaricato molto in poco tempo. Riprova tra {n} minuti.",
  downloadMissing: "Alcune di quelle foto non sono più disponibili e sono state tolte dalla selezione.",
  filterByAlbum: "Filtra le foto per album",
  filterByTag: "Filtra le foto per etichetta",
  chooseToDownload: "Scegli le foto da scaricare",
  downloadSelection: "Scarica la selezione",
  noTagged: "Ancora nessuna foto con l'etichetta «{tag}»",
  emptyAlbum: "L'album è vuoto",
  tryAnotherTag: "Prova un'altra etichetta o scegli Tutte per vedere ogni foto.",
  emptyHint: "Ancora niente. Le foto compaiono quando i padroni di casa le approvano.",
  showMoreLeft: "Mostra altro (ne restano {n})",
  notFound: "Non trovato",
  noGallery: "A questo indirizzo non c'è nessuna galleria.",
  galleryLoadFailed: "Impossibile caricare questa galleria.",
  openingGallery: "Apertura della galleria…",
  privateGallery: "Galleria privata",
  galleryIsPrivate: "Questa galleria è privata",
  enterPassword: "Inserisci la password che ti hanno dato i padroni di casa.",
  passwordLabel: "Password",
  passwordPlaceholder: "Password dell'evento",
  openGallery: "Apri la galleria",
  requiresPassword: "Questo evento richiede una password",
  wrongPassword: "La password non è corretta.",
  enter: "Entra",
  addYourFace: "Trova te stesso",
  selfieHint: "Aggiungi un selfie e cercheremo nell'album le foto in cui ci sei. Il selfie non lascia mai questo telefono: la ricerca avviene qui, non su un server.",
  takeSelfie: "Aggiungi un selfie",
  scanning: "Ti stiamo cercando…",
  noMatchYet: "Ancora nessun risultato. Prova con una foto più nitida.",
  matches: "Foto in cui ci sei",
  photoDialog: "Foto",
  eventPhoto: "Foto dell'evento",
  untitled: "Senza titolo",
  previousPhoto: "Foto precedente",
  nextPhoto: "Foto successiva",
  sharedBy: "Condivisa da {name}",
  photoTags: "Etichette della foto",
  autoDescription: "Descrizione automatica",
  aiFlagged: "Segnalata per una verifica. Questa foto è sfocata nella galleria.",
  like: "Mi piace",
  liked: "Ti piace",
  doubleTapToLike: "Tocca due volte la foto per mettere mi piace",
  comment: "Commenta",
  comments: "Commenti",
  noComments: "Ancora niente.",
  you: "(tu)",
  deleteCommentFrom: "Elimina il commento di {name}",
  deleting: "Eliminazione…",
  delete: "Elimina",
  addComment: "Aggiungi un commento",
  commentPlaceholder: "Scrivi qualcosa…",
  post: "Pubblica",
  hostCanRemove: "Sei l'organizzatore, quindi puoi rimuovere qualsiasi commento.",
  saveFailed: "Impossibile salvare.",
  postFailed: "Impossibile pubblicare.",
  deleteFailed: "Impossibile eliminare.",
  selectPhoto: "Seleziona {name}",
  hiddenByHost: "Nascosta dai padroni di casa",
  couldNotLoad: "Impossibile caricare",
  pinned: "In evidenza",
  pinnedToWall: "In evidenza sullo schermo",
  guestbook: "Libro degli ospiti",
  guestbookPlaceholder: "Lascia un messaggio ai padroni di casa",
  record: "Registra un messaggio vocale",
  stop: "Stop",
  send: "Invia",
  sending: "Invio…",
  emptyGuestbook: "Ancora nessun messaggio. Scrivi tu il primo!",
  removeMine: "Rimuovi il mio messaggio",
  waitingForHost: "In attesa dei padroni di casa",
  heldNotice: "Grazie! Il tuo messaggio comparirà quando i padroni di casa l'avranno visto.",
  previewHint: "Riascoltalo prima di inviarlo.",
  sendVoice: "Invia il messaggio vocale",
  recordAgain: "Registra di nuovo",
  discard: "Scarta",
  micDenied: "L'accesso al microfono è stato bloccato. Consentilo nelle impostazioni del sito del browser e riprova.",
  wallScan: "Inquadra per aggiungere le tue foto",
  wallFirstPhoto: "Inquadra per aggiungere la prima foto.",
  wallWaiting: "In attesa della prima foto.",
  slideShow: "Ti stai divertendo? Aggiungi le tue foto",
  errRateLimited: "È tanto tutto insieme. Riprova tra poco.",
  errRateLimitedEvent: "Questo evento sta ricevendo molte foto in questo momento. Riprova tra poco.",
  errTooLarge: "Questo file è troppo grande da inviare.",
  errUnsupported: "Questo tipo di file non si può aggiungere qui.",
  errQuota: "I padroni di casa hanno finito lo spazio, quindi ora questo evento non può ricevere foto.",
  errClosed: "Questo evento non accetta più nuovi contributi.",
  errNotFound: "Questo link non esiste, oppure l'evento è stato rimosso.",
  errForbidden: "Non è consentito per questo evento.",
  errOffline: "Sembra che tu sia offline. Controlla la connessione e riprova.",
};

const pt: Dictionary = {
  loading: "A carregar…",
  oneMoment: "Um momento.",
  error: "Algo correu mal. Tente novamente.",
  footer: "Com Eventgram · As suas fotos ficam privadas neste evento",
  languageLabel: "Idioma",
  privateAlbum: "Álbum privado",
  optional: "opcional",
  remove: "Remover",
  clear: "Limpar",
  cancel: "Cancelar",
  close: "Fechar",
  all: "Todas",
  photo: "Foto",
  showMore: "Ver mais",
  uploadTitle: "Partilhe as suas fotos",
  uploadHint: "Aparecem na festa de imediato",
  chooseFiles: "Escolher fotos",
  camera: "Câmara",
  sendQueue: "Enviar",
  uploading: "A enviar…",
  addedToGallery: "Enviado! Obrigado.",
  addedToGalleryDetail: "Já estão com os anfitriões. Pode adicionar mais quando quiser.",
  captionPlaceholder: "Adicione uma legenda (opcional)",
  captionFor: "Legenda para {name}",
  uploadingFile: "A enviar {name}",
  namePlaceholder: "O seu nome (opcional)",
  nameExample: "Inês",
  filterLabel: "Estilo",
  albumLabel: "Álbum",
  mainAlbum: "Galeria principal",
  openingAlbum: "A abrir o álbum…",
  linkNotFound: "Ligação não encontrada",
  linkDoesNotExist: "Essa ligação do evento não existe.",
  firstPhoto: "Seja a primeira pessoa a adicionar uma foto",
  photosInAlbumOne: "{n} foto no álbum",
  photosInAlbumOther: "{n} fotos no álbum",
  uploadLimitHint: "Fotos e vídeos até 25 MB. Nada é enviado até tocar em Enviar.",
  sentProgress: "{done} de {total} enviadas",
  statusReady: "Pronta",
  statusSending: "A enviar",
  statusSent: "Enviada",
  statusFailed: "Falhou",
  retryFile: "Tentar este ficheiro outra vez",
  attempts: "{n} tentativas",
  sentFromPhone: "Enviadas deste telemóvel",
  waitingApproval: "À espera da aprovação dos anfitriões",
  notOnWall: "Não aparece no ecrã",
  onWall: "No ecrã",
  uploadNetworkError: "A ligação caiu durante o envio. Tente novamente.",
  uploadStalled: "O envio deixou de responder. Tente novamente.",
  uploadCancelled: "Envio cancelado.",
  uploadFailed: "O envio não foi concluído. Tente novamente.",
  fileTypeRefused: "Escolha uma foto ou um vídeo (JPEG, PNG, WebP, GIF, HEIC, MP4, MOV ou WebM).",
  fileTooLarge: "Mais de 25 MB",
  viewGallery: "Ver a galeria",
  galleryTitle: "Galeria do evento",
  hostView: "Vista de anfitrião",
  photoCountOne: "{n} foto",
  photoCountOther: "{n} fotos",
  taggedWith: "com a etiqueta «{tag}»",
  eventFinished: "este evento terminou",
  downloadAll: "Transferir tudo",
  downloadAllPhotos: "Transferir estas fotos",
  preparingDownload: "A preparar a transferência…",
  selectPhotos: "Selecionar fotos",
  selectedOne: "{n} foto selecionada",
  selectedOther: "{n} fotos selecionadas",
  selectAllShown: "Selecionar as visíveis",
  downloadSelected: "Transferir seleção ({n})",
  downloadStarting: "A transferência está a começar. Um álbum grande pode demorar um pouco.",
  downloadFailed: "Não foi possível iniciar a transferência. Tente novamente.",
  downloadTooLarge: "É mais do que cabe numa transferência. Selecione antes as fotos que quer.",
  downloadWaitOne: "Transferiu muito há pouco. Tente novamente daqui a {n} minuto.",
  downloadWaitOther: "Transferiu muito há pouco. Tente novamente daqui a {n} minutos.",
  downloadMissing: "Algumas dessas fotos já não estão disponíveis e foram retiradas da sua seleção.",
  filterByAlbum: "Filtrar fotos por álbum",
  filterByTag: "Filtrar fotos por etiqueta",
  chooseToDownload: "Escolher fotos para transferir",
  downloadSelection: "Transferir a seleção",
  noTagged: "Ainda não há fotos com a etiqueta «{tag}»",
  emptyAlbum: "O álbum está vazio",
  tryAnotherTag: "Experimente outra etiqueta ou escolha Todas para ver todas as fotos.",
  emptyHint: "Ainda não há nada. As fotos aparecem quando os anfitriões as aprovam.",
  showMoreLeft: "Ver mais (faltam {n})",
  notFound: "Não encontrado",
  noGallery: "Não existe nenhuma galeria neste endereço.",
  galleryLoadFailed: "Não foi possível carregar esta galeria.",
  openingGallery: "A abrir a galeria…",
  privateGallery: "Galeria privada",
  galleryIsPrivate: "Esta galeria é privada",
  enterPassword: "Introduza a palavra-passe que os anfitriões lhe deram.",
  passwordLabel: "Palavra-passe",
  passwordPlaceholder: "Palavra-passe do evento",
  openGallery: "Abrir a galeria",
  requiresPassword: "Este evento precisa de uma palavra-passe",
  wrongPassword: "A palavra-passe está errada.",
  enter: "Entrar",
  addYourFace: "Encontre-se",
  selfieHint: "Adicione uma selfie e procuramos no álbum as fotos em que aparece. A selfie nunca sai deste telemóvel: a pesquisa é feita aqui, não num servidor.",
  takeSelfie: "Adicionar uma selfie",
  scanning: "À sua procura…",
  noMatchYet: "Ainda nada encontrado. Experimente uma foto mais nítida.",
  matches: "Fotos em que aparece",
  photoDialog: "Foto",
  eventPhoto: "Foto do evento",
  untitled: "Sem título",
  previousPhoto: "Foto anterior",
  nextPhoto: "Foto seguinte",
  sharedBy: "Partilhada por {name}",
  photoTags: "Etiquetas da foto",
  autoDescription: "Descrição automática",
  aiFlagged: "Assinalada para revisão. Esta foto aparece desfocada na galeria.",
  like: "Gosto",
  liked: "Gostou",
  doubleTapToLike: "Toque duas vezes na foto para gostar",
  comment: "Comentar",
  comments: "Comentários",
  noComments: "Ainda não há nada.",
  you: "(você)",
  deleteCommentFrom: "Apagar o comentário de {name}",
  deleting: "A apagar…",
  delete: "Apagar",
  addComment: "Adicionar um comentário",
  commentPlaceholder: "Escreva algo…",
  post: "Publicar",
  hostCanRemove: "É o anfitrião, por isso pode remover qualquer comentário.",
  saveFailed: "Não foi possível guardar.",
  postFailed: "Não foi possível publicar.",
  deleteFailed: "Não foi possível apagar.",
  selectPhoto: "Selecionar {name}",
  hiddenByHost: "Ocultada pelos anfitriões",
  couldNotLoad: "Não foi possível carregar",
  pinned: "Fixada",
  pinnedToWall: "Fixada no ecrã",
  guestbook: "Livro de visitas",
  guestbookPlaceholder: "Deixe uma mensagem aos anfitriões",
  record: "Gravar uma mensagem de voz",
  stop: "Parar",
  send: "Enviar",
  sending: "A enviar…",
  emptyGuestbook: "Ainda não há mensagens. Seja a primeira pessoa!",
  removeMine: "Remover a minha mensagem",
  waitingForHost: "À espera dos anfitriões",
  heldNotice: "Obrigado! A sua mensagem aparece quando os anfitriões a tiverem visto.",
  previewHint: "Ouça antes de enviar.",
  sendVoice: "Enviar mensagem de voz",
  recordAgain: "Gravar outra vez",
  discard: "Descartar",
  micDenied: "O acesso ao microfone foi bloqueado. Permita-o nas definições do site do seu navegador e tente novamente.",
  wallScan: "Leia o código para adicionar as suas fotos",
  wallFirstPhoto: "Leia o código para adicionar a primeira foto.",
  wallWaiting: "À espera da primeira foto.",
  slideShow: "Está a gostar da festa? Adicione as suas fotos",
  errRateLimited: "É muita coisa de uma vez. Tente novamente daqui a pouco.",
  errRateLimitedEvent: "Este evento está recebendo muitas fotos agora. Tente novamente daqui a pouco.",
  errTooLarge: "Esse ficheiro é demasiado grande para enviar.",
  errUnsupported: "Esse tipo de ficheiro não pode ser adicionado aqui.",
  errQuota: "Os anfitriões ficaram sem espaço, por isso este evento não pode receber fotos agora.",
  errClosed: "Este evento já não aceita novas contribuições.",
  errNotFound: "Essa ligação não existe ou o evento foi removido.",
  errForbidden: "Isso não é permitido neste evento.",
  errOffline: "Parece estar sem ligação. Verifique a ligação e tente novamente.",
};

/** Exported for verify:i18n, which checks key parity and placeholder parity. */
export const DICTIONARIES: Readonly<Record<Locale, Dictionary>> = { en, es, fr, de, it, pt };

export type GuestCopy = Dictionary;

const STORAGE_KEY = "eg-guest-lang";
const CHANGE_EVENT = "eg-guest-lang-change";

function asLocale(value: string | null | undefined): Locale | null {
  const code = (value ?? "").trim().toLowerCase().slice(0, 2);
  return (LOCALES as readonly string[]).includes(code) ? (code as Locale) : null;
}

function storedChoice(): Locale | null {
  try {
    return asLocale(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

function browserChoice(): Locale | null {
  const preferences = typeof navigator !== "undefined" ? (navigator.languages ?? [navigator.language]) : [];
  for (const preference of preferences) {
    const match = asLocale(preference);
    if (match) return match;
  }
  return null;
}

/** The locale a guest sees, by the precedence in this file's header. */
export function resolveLocale(eventLanguage: string, search = typeof location !== "undefined" ? location.search : ""): Locale {
  return (
    asLocale(new URLSearchParams(search).get("lang")) ??
    storedChoice() ??
    asLocale(eventLanguage) ??
    browserChoice() ??
    "en"
  );
}

/** The dictionary for an event's chosen language, without the URL or a stored pick. */
export function dictionaryFor(eventLanguage: string): Dictionary {
  return DICTIONARIES[asLocale(eventLanguage) ?? browserChoice() ?? "en"];
}

/** Fill `{name}` placeholders. Unknown placeholders are left visible, not dropped. */
export function format(template: string, values: Readonly<Record<string, string | number>>): string {
  return template.replace(/\{(\w+)\}/g, (whole, key: string) =>
    key in values ? String(values[key]) : whole,
  );
}

type PluralBase = {
  [K in GuestCopyKey]: K extends `${infer Base}One` ? (`${Base}Other` extends GuestCopyKey ? Base : never) : never;
}[GuestCopyKey];

export interface GuestLocale {
  readonly lang: Locale;
  readonly copy: Dictionary;
  /** Persist a choice on this device and put it in the address, without a reload. */
  setLang(next: Locale): void;
  /** `copy[key]` with its placeholders filled. */
  t(key: GuestCopyKey, values?: Readonly<Record<string, string | number>>): string;
  /** The `…One` / `…Other` pair for `n`, with `{n}` and any other values filled. */
  plural(base: PluralBase, n: number, values?: Readonly<Record<string, string | number>>): string;
  /** A calendar date (YYYY-MM-DD) in the guest's language. */
  date(value: string): string;
  /** A timestamp in the guest's language. */
  dateTime(value: string): string;
}

export function localeFor(lang: Locale, setLang: (next: Locale) => void = () => undefined): GuestLocale {
  const copy = DICTIONARIES[lang];
  const rules = new Intl.PluralRules(lang);
  return {
    lang,
    copy,
    setLang,
    t: (key, values) => (values ? format(copy[key], values) : copy[key]),
    plural: (base, n, values) => {
      const key = `${base}${rules.select(n) === "one" ? "One" : "Other"}` as GuestCopyKey;
      return format(copy[key], { n: new Intl.NumberFormat(lang).format(n), ...values });
    },
    date: (value) => {
      const parsed = new Date(`${value}T00:00:00`);
      return Number.isNaN(parsed.getTime())
        ? value
        : parsed.toLocaleDateString(lang, { day: "numeric", month: "long" });
    },
    dateTime: (value) => {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime())
        ? value
        : parsed.toLocaleString(lang, { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
    },
  };
}

/**
 * The guest's locale, live.
 *
 * Re-resolves when the guest picks a language (in this tab or another) or the
 * address changes, and keeps `<html lang>` in step so a screen reader reads
 * the page in the language it is written in.
 */
export function useGuestLocale(eventLanguage: string): GuestLocale {
  const [lang, setLangState] = useState<Locale>(() => resolveLocale(eventLanguage));

  useEffect(() => {
    const refresh = (): void => setLangState(resolveLocale(eventLanguage));
    refresh();
    window.addEventListener(CHANGE_EVENT, refresh);
    window.addEventListener("popstate", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh);
      window.removeEventListener("popstate", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, [eventLanguage]);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next: Locale): void => {
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* private mode: the address still carries the choice */
    }
    // The address carries the choice too, so a link copied now opens in the
    // same language. replaceState, not navigate: no reload, no history entry.
    const url = new URL(window.location.href);
    url.searchParams.set("lang", next);
    window.history.replaceState(window.history.state, "", url);
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  return useMemo(() => localeFor(lang, setLang), [lang, setLang]);
}

/** Kept for callers that only need the dictionary. */
export function useCopy(eventLanguage: string): GuestCopy {
  return useGuestLocale(eventLanguage).copy;
}

/**
 * A server or network error, in the guest's language.
 *
 * Mapped by the API's error *code*, never by its English message: the message
 * is for logs and the host dashboard. Anything without a mapping gets the
 * generic sentence rather than English.
 */
export function guestErrorMessage(copy: Dictionary, error: unknown): string {
  const candidate = error as { code?: unknown } | null;
  const code = typeof candidate?.code === "string" ? candidate.code : "";

  // fetch() rejects with a TypeError when the request never got an answer.
  if (!code && error instanceof TypeError) return copy.errOffline;
  switch (code) {
    case "rate_limited": {
      // The server says whether the guest's device or the whole event hit the
      // ceiling. Both sentences are generic enough not to blame a guest who did
      // nothing wrong, but the event-wide one stops a room full of people
      // retrying a limit their own phones did not cause.
      const details = (error as { details?: { scope?: unknown } } | null)?.details;
      return details?.scope === "event" ? copy.errRateLimitedEvent : copy.errRateLimited;
    }
    case "payload_too_large":
      return copy.errTooLarge;
    case "unsupported_media_type":
      return copy.errUnsupported;
    case "quota_exceeded":
      return copy.errQuota;
    case "conflict":
      return copy.errClosed;
    case "not_found":
      return copy.errNotFound;
    case "unauthorized":
      return copy.wrongPassword;
    case "forbidden":
      return copy.errForbidden;
    default:
      return copy.error;
  }
}
